import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ConfirmDeleteModal } from '@/components/ConfirmDeleteModal';
import { FixButton } from '@/components/FixButton';
import { FolderOpen, Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronUp, CalendarIcon, Star } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseDateKey } from '@/types';

const IMPORTANCE_LEVELS = [
  { value: 'ultra', label: 'Ultra importante', stars: 3, bg: 'hsl(220, 80%, 40%)', cls: 'bg-[hsl(220,80%,40%)]' },
  { value: 'muy', label: 'Muy importante', stars: 2, bg: 'hsl(210, 70%, 55%)', cls: 'bg-[hsl(210,70%,55%)]' },
  { value: 'algo', label: 'Algo importante', stars: 1, bg: 'hsl(210, 50%, 70%)', cls: 'bg-[hsl(210,50%,70%)]' },
];

function getLevel(importance) {
  return IMPORTANCE_LEVELS.find(l => l.value === importance) || IMPORTANCE_LEVELS[2];
}

function ImportanceBadge({ importance }) {
  const lvl = getLevel(importance);
  return (
    <span
      className="inline-flex items-center gap-px px-1.5 py-0.5 rounded text-white shrink-0"
      style={{ backgroundColor: lvl.bg, fontSize: '0.625rem', lineHeight: 1 }}
      title={lvl.label}
      data-testid={`importance-badge-${importance}`}
    >
      {'★'.repeat(lvl.stars)}
    </span>
  );
}

function ImportanceSelector({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="importance-selector">
      {IMPORTANCE_LEVELS.map(lvl => (
        <button
          key={lvl.value}
          type="button"
          className="px-2 py-1 rounded text-white text-xs transition-all"
          style={{
            backgroundColor: lvl.bg,
            opacity: value === lvl.value ? 1 : 0.35,
            outline: value === lvl.value ? '2px solid white' : 'none',
            outlineOffset: '1px',
          }}
          onClick={() => onChange(lvl.value)}
          title={lvl.label}
          data-testid={`importance-${lvl.value}`}
        >
          {'★'.repeat(lvl.stars)}
        </button>
      ))}
    </div>
  );
}

function DeadlineBadge({ deadline }) {
  if (!deadline) return null;
  try {
    const d = parseDateKey(deadline);
    const today = new Date(); today.setHours(0,0,0,0);
    const daysLeft = differenceInDays(d, today);
    let color;
    if (daysLeft <= 0) color = 'hsl(0, 70%, 45%)';
    else if (daysLeft <= 7) color = 'hsl(30, 80%, 50%)';
    else color = 'hsl(220, 70%, 50%)';
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white text-xs shrink-0"
        style={{ backgroundColor: color, fontSize: '0.65rem' }}
      >
        <CalendarIcon className="h-2.5 w-2.5" />
        {format(d, 'd MMM', { locale: es })}
      </span>
    );
  } catch { return null; }
}

export function ProjectsPanel({ projects, onCreateProject, onUpdateProject, onDeleteProject }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [objectives, setObjectives] = useState('');
  const [characteristics, setCharacteristics] = useState('');
  const [notes, setNotes] = useState('');
  const [deadline, setDeadline] = useState(() => format(addDays(new Date(), 28), 'yyyy-MM-dd'));
  const [importance, setImportance] = useState('algo');
  const [showCalendar, setShowCalendar] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editCalendar, setEditCalendar] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canCreate = title.trim() && deadline;

  const handleCreate = () => {
    if (canCreate) {
      onCreateProject(title.trim(), objectives.trim(), characteristics.trim(), notes.trim(), deadline, importance);
      setTitle(''); setObjectives(''); setCharacteristics(''); setNotes('');
      setDeadline(format(addDays(new Date(), 28), 'yyyy-MM-dd'));
      setImportance('algo');
      setShowForm(false);
    }
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditData({
      title: project.title,
      objectives: project.objectives || '',
      characteristics: project.characteristics || '',
      notes: project.notes || '',
      deadline: project.deadline || format(addDays(new Date(), 28), 'yyyy-MM-dd'),
      importance: project.importance || 'algo',
    });
  };

  const saveEdit = () => {
    if (editData.title?.trim() && editData.deadline) {
      onUpdateProject(editingId, editData);
      setEditingId(null); setEditData({});
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  return (
    <Card data-testid="projects-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FolderOpen className="h-4 w-4" />
          Proyectos
          <span className="ml-auto text-xs text-muted-foreground">
            {projects.length} proyecto{projects.length !== 1 ? 's' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Create form */}
        {!showForm ? (
          <button
            className="add-entry-btn w-full justify-center border border-dashed border-border rounded-md py-2"
            onClick={() => setShowForm(true)}
            data-testid="new-project-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Nuevo proyecto
          </button>
        ) : (
          <div className="project-form" data-testid="project-form">
            <input
              type="text"
              placeholder="Título del proyecto..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) setShowCalendar(true); }}
              className="project-input"
              autoFocus
              data-testid="project-title-input"
            />

            <div className="flex items-center gap-2 flex-wrap">
              <Popover open={showCalendar} onOpenChange={(o) => { if (!o) setShowCalendar(false); }}>
                <PopoverTrigger asChild>
                  <button
                    className="project-date-btn"
                    onClick={() => setShowCalendar(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowCalendar(true); } }}
                    data-testid="project-deadline-btn"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                    <span>{deadline ? format(parseDateKey(deadline), 'd MMM yyyy', { locale: es }) : 'Fecha de entrega *'}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={deadline ? parseDateKey(deadline) : addDays(new Date(), 28)}
                    defaultMonth={deadline ? parseDateKey(deadline) : addDays(new Date(), 28)}
                    onSelect={(date) => { if (date) setDeadline(format(date, 'yyyy-MM-dd')); setShowCalendar(false); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <ImportanceSelector value={importance} onChange={setImportance} />
            </div>

            {!deadline && <span className="text-xs text-red-400">La fecha de entrega es obligatoria</span>}

            <textarea placeholder="Objetivos..." value={objectives} onChange={(e) => setObjectives(e.target.value)} className="project-textarea" rows={2} data-testid="project-objectives-input" />
            <textarea placeholder="Características..." value={characteristics} onChange={(e) => setCharacteristics(e.target.value)} className="project-textarea" rows={2} data-testid="project-characteristics-input" />
            <textarea placeholder="Notas..." value={notes} onChange={(e) => setNotes(e.target.value)} className="project-textarea" rows={2} data-testid="project-notes-input" />

            <div className="flex gap-2">
              <button className="project-btn primary" onClick={handleCreate} disabled={!canCreate} data-testid="save-project-btn">
                <Check className="h-3.5 w-3.5" /> Crear proyecto
              </button>
              <button className="project-btn" onClick={() => setShowForm(false)}>
                <X className="h-3.5 w-3.5" /> Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {projects.length === 0 && !showForm && (
          <p className="text-xs text-muted-foreground italic text-center py-4">Sin proyectos</p>
        )}

        {/* Project list */}
        <div className="space-y-2">
          {projects.map((project) => (
            <div key={project.id} className="project-card group" data-testid={`project-${project.id}`}>
              {editingId === project.id ? (
                <div className="project-form">
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="project-input"
                    autoFocus
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Popover open={editCalendar} onOpenChange={(o) => { if (!o) setEditCalendar(false); }}>
                      <PopoverTrigger asChild>
                        <button
                          className="project-date-btn"
                          onClick={() => setEditCalendar(true)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditCalendar(true); } }}
                        >
                          <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                          <span>{editData.deadline ? format(parseDateKey(editData.deadline), 'd MMM yyyy', { locale: es }) : 'Fecha de entrega *'}</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={editData.deadline ? parseDateKey(editData.deadline) : addDays(new Date(), 28)}
                          defaultMonth={editData.deadline ? parseDateKey(editData.deadline) : addDays(new Date(), 28)}
                          onSelect={(date) => { if (date) setEditData({ ...editData, deadline: format(date, 'yyyy-MM-dd') }); setEditCalendar(false); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <ImportanceSelector value={editData.importance || 'algo'} onChange={(v) => setEditData({ ...editData, importance: v })} />
                  </div>
                  <textarea placeholder="Objetivos..." value={editData.objectives} onChange={(e) => setEditData({ ...editData, objectives: e.target.value })} className="project-textarea" rows={2} />
                  <textarea placeholder="Características..." value={editData.characteristics} onChange={(e) => setEditData({ ...editData, characteristics: e.target.value })} className="project-textarea" rows={2} />
                  <textarea placeholder="Notas..." value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} className="project-textarea" rows={2} />
                  <div className="flex gap-2">
                    <button className="project-btn primary" onClick={saveEdit} disabled={!editData.title?.trim() || !editData.deadline}>
                      <Check className="h-3.5 w-3.5" /> Guardar
                    </button>
                    <button className="project-btn" onClick={cancelEdit}>
                      <X className="h-3.5 w-3.5" /> Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="project-header" onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}>
                    <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <ImportanceBadge importance={project.importance} />
                    <span className="project-title">{project.title}</span>
                    <DeadlineBadge deadline={project.deadline} />
                    <div className="project-actions opacity-0 group-hover:opacity-100">
                      <button className="icon-btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(project); }}>
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        className="icon-btn-sm text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(project); }}
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    {expandedId === project.id ? (
                      <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    )}
                  </div>

                  {expandedId === project.id && (
                    <div className="project-details">
                      {project.objectives && (
                        <div className="project-section">
                          <div className="flex items-center gap-2">
                            <span className="project-section-label">Objetivos</span>
                            <FixButton
                              text={project.objectives}
                              onApply={(fixed) => onUpdateProject(project.id, { objectives: fixed })}
                              voice="first_person"
                            />
                          </div>
                          <p className="project-section-text">{project.objectives}</p>
                        </div>
                      )}
                      {project.characteristics && (
                        <div className="project-section">
                          <div className="flex items-center gap-2">
                            <span className="project-section-label">Características</span>
                            <FixButton
                              text={project.characteristics}
                              onApply={(fixed) => onUpdateProject(project.id, { characteristics: fixed })}
                              voice="first_person"
                            />
                          </div>
                          <p className="project-section-text">{project.characteristics}</p>
                        </div>
                      )}
                      {project.notes && (
                        <div className="project-section">
                          <span className="project-section-label">Notas</span>
                          <p className="project-section-text">{project.notes}</p>
                        </div>
                      )}
                      {!project.objectives && !project.characteristics && !project.notes && (
                        <p className="text-xs text-muted-foreground italic">Sin detalles</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <ConfirmDeleteModal
          open={!!deleteTarget}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          title={`¿Eliminar proyecto "${deleteTarget?.title}"?`}
          onConfirm={() => { onDeleteProject(deleteTarget.id); setDeleteTarget(null); }}
        />
      </CardContent>
    </Card>
  );
}
